/**
 * Pinned projects — the same star Flow and the Projects app keep (projects-back
 * `project.favorite.list` / `project.favorite.toggle` through the gateway), so a project pinned in
 * one app is pinned in all. Artwork has no sidebar: pinned projects lead the Home as their own row.
 *
 * One store for the page: Pin and Unpin change it at once (the call follows; a refusal puts it
 * back), so every card, menu and the Home's row agree without a re-read.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { gatewayCall } from '../share/gateway';

export interface Pin {
  readonly id: string;
  readonly name: string;
  readonly cover: string | null;
}

let pins: readonly Pin[] | null = null;
let readFor: string | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((one) => one());
const set = (next: readonly Pin[] | null) => {
  pins = next;
  emit();
};

type Loose = Record<string, unknown>;
const text = (value: unknown) => (typeof value === 'string' && value ? value : null);

function readPins(raw: unknown): Pin[] {
  const rows = Array.isArray(raw) ? raw : Array.isArray((raw as Loose)?.data) ? ((raw as Loose).data as unknown[]) : Array.isArray((raw as Loose)?.items) ? ((raw as Loose).items as unknown[]) : [];
  const out: Pin[] = [];
  for (const one of rows) {
    if (!one || typeof one !== 'object') continue;
    const row = one as Loose;
    if (row.isFavorite === false) continue;
    const id = text(row.projectId) ?? text(row.id);
    if (!id) continue;
    out.push({ id, name: text(row.projectName) ?? text(row.name) ?? 'Untitled Project', cover: text(row.coverImageUrl) });
  }
  return out;
}

/** Reads the pinned list for this workspace (once per workspace; `force` asks again). */
export function loadPins(workspace: string, force = false): void {
  if (!force && readFor === workspace) return;
  readFor = workspace;
  if (!force) set(null);
  gatewayCall('projects', 'project.favorite.list', { limit: 50 }, { method: 'get' }).then(
    (raw) => readFor === workspace && set(readPins(raw)),
    () => readFor === workspace && set(pins ?? []),
  );
}

/** Pins or unpins a project at once; the call follows, and a refusal puts it back. */
export async function setPinned(project: Pin, on: boolean): Promise<void> {
  const was = pins ?? [];
  const has = was.some((one) => one.id === project.id);
  if (has === on) return;
  set(on ? [project, ...was] : was.filter((one) => one.id !== project.id));
  try {
    const res = (await gatewayCall<{ isFavorite?: boolean }>('projects', 'project.favorite.toggle', { projectId: project.id })) ?? {};
    // The server's star was already the other way: toggle once more so it ends where it was asked.
    if (typeof res.isFavorite === 'boolean' && res.isFavorite !== on) await gatewayCall('projects', 'project.favorite.toggle', { projectId: project.id });
  } catch (err) {
    set(was);
    throw err;
  }
}

/** The pinned projects (null while the first read is out), read for `workspace`. */
export function usePins(workspace: string): readonly Pin[] | null {
  useEffect(() => {
    if (workspace) loadPins(workspace);
  }, [workspace]);
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => pins,
  );
}
